// photoFit — decides cover vs contain for each photo / slot pair.
// Shared between the PDF generator (web/src/lib/pdfGenerator.ts) and the
// editor's PhotoSlot rendering, so the editor preview is truthful: what
// you see is what the PDF prints.
//
// Today every photo is COVER-fit: image scaled to fill the slot, edges
// cropped. That works for landscape on landscape, but mangles:
//   - Portrait photos in landscape slots (top + bottom faces chopped off)
//   - Group photos with faces near edges
//   - Multi-face composites where every face matters
//
// CONTAIN-fit shows the full image scaled to fit, with a cream letterbox
// in the unused slot area. We pick contain when ANY of these hold:
//   1. face_count ≥ 3            — likely a group photo
//   2. face_spread > 0.5         — faces fill > half the image
//   3. aspect_ratio mismatch     — image vs slot shape differs > 30%

export type FitMode = 'cover' | 'contain';

export interface PhotoFitInput {
  width: number | null;
  height: number | null;
  face_count: number | null;
  face_spread: number | null;
  aspect_ratio?: 'landscape' | 'portrait' | 'square' | null;
}

export interface SlotFitInput {
  width: number;
  height: number;
}

// Cover-fit by default — "no white space in grids" is the stated preference.
// We fall back to contain only when cropping would visibly slice a face:
//   1. 4+ faces (group photo) — the face-aware crop centroid can't keep all
//      of them visible at once.
//   2. face_spread > 0.6 — one or two faces fill so much of the frame that
//      cropping is guaranteed to lose part of them.
//   3. Big aspect mismatch between photo and slot AND the photo has at least
//      one face — wide landscape in a portrait slot, or vice versa, means
//      cover would crop hard on one axis, and there's an actual face to
//      protect. Threshold (50%) is intentionally generous: small mismatches
//      where the face-aware crop can still keep the head in view stay cover.
const FACE_COUNT_THRESHOLD = 4;
const FACE_SPREAD_THRESHOLD = 0.6;
// Unit C: lowered from 0.50 → 0.35 so we letterbox (cream pad) earlier on
// borderline portrait-in-landscape-slot situations. Cover-crop at 0.50
// was clipping faces near the slot edge often enough to be noticeable;
// 0.35 means a typical 3:4 portrait in a 4:3 slot (~14% mismatch) still
// covers, but a 3:4 portrait in a 16:9 slot (~30% mismatch) letterboxes.
const ASPECT_MISMATCH_THRESHOLD = 0.35;

export function decideFitMode(photo: PhotoFitInput, slot: SlotFitInput): FitMode {
  if (photo.face_count != null && photo.face_count >= FACE_COUNT_THRESHOLD) return 'contain';
  if (photo.face_spread != null && photo.face_spread > FACE_SPREAD_THRESHOLD) return 'contain';
  // Aspect-mismatch + has-faces guard: only protect against cropping when
  // there's an actual face that could get cut.
  const hasFace = (photo.face_count ?? 0) > 0;
  if (hasFace && photo.width && photo.height && slot.width && slot.height) {
    const photoRatio = photo.width / photo.height;
    const slotRatio = slot.width / slot.height;
    const mismatch = Math.abs(photoRatio - slotRatio) / Math.max(photoRatio, slotRatio);
    if (mismatch > ASPECT_MISMATCH_THRESHOLD) return 'contain';
  }
  return 'cover';
}

/**
 * For PDF rendering: returns x/y/width/height for drawImage in CONTAIN mode.
 * Image is centered in the slot, scaled to fit fully without crop.
 */
export function computeContainDrawParams(
  slotX: number, slotY: number, slotW: number, slotH: number,
  imgW: number, imgH: number,
): { x: number; y: number; width: number; height: number } {
  const scale = Math.min(slotW / imgW, slotH / imgH);
  const w = imgW * scale;
  const h = imgH * scale;
  return {
    x: slotX + (slotW - w) / 2,
    y: slotY + (slotH - h) / 2,
    width: w,
    height: h,
  };
}

/**
 * Letterbox color used to fill the unused area of a contain-fit slot.
 * Same cream as the editor background so the empty space reads as
 * intentional design rather than missing image.
 */
export const LETTERBOX_FILL = { r: 0.965, g: 0.941, b: 0.886 }; // #F6F0E2 (BRAND.cream)

// ─── Face-safe crop verification ─────────────────────────────────────────────
// Unit B: faces are protected as a HARD constraint, not a soft hint. After
// the renderer picks a crop centre (face centroid or customer pan), we run
// the candidate crop through this verifier to make sure every detected face
// lands within the inner SAFE_ZONE of the slot. If any face's bbox crosses
// the inset, we reject the crop and the caller switches to contain-fit
// (cream letterbox) so no face gets sliced. The inner 85% (= 7.5% inset on
// every side) is a print-bindery convention — enough margin that the cut
// line + finger-shadow at a face edge won't be jarring.
export const SAFE_ZONE_INSET = 0.075;

export interface FaceBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Given a cover-fit crop centred at (cropX%, cropY%), check that every
 * face bbox in `faceData` lands inside the inner (1 - 2*inset) of the
 * slot. Returns true when there are no faces to protect (a scenery shot
 * has nothing to clip) or when every face fits safely.
 *
 * Math: cover-fit scales the image so the SHORT axis fills the slot; the
 * LONG axis overhangs and gets cropped. The visible image-percentage on
 * each axis is (slot / drawn) × 100, centred on (cropX, cropY). A face's
 * image-percent position is mapped into [0..1] slot coordinates via that
 * window, then checked against the inset.
 */
export function isCropSafe(
  faceData: unknown,
  imgW: number,
  imgH: number,
  slotW: number,
  slotH: number,
  cropX: number,
  cropY: number,
  inset = SAFE_ZONE_INSET,
): boolean {
  const faces = Array.isArray(faceData) ? (faceData as FaceBox[]) : null;
  if (!faces?.length || !imgW || !imgH || !slotW || !slotH) return true;

  const scale = Math.max(slotW / imgW, slotH / imgH);
  const drawnW = imgW * scale;
  const drawnH = imgH * scale;
  // Visible image-percentage on each axis (always ≤ 100).
  const visWPct = Math.min(100, (slotW / drawnW) * 100);
  const visHPct = Math.min(100, (slotH / drawnH) * 100);
  const visX0 = cropX - visWPct / 2;
  const visX1 = cropX + visWPct / 2;
  const visY0 = cropY - visHPct / 2;
  const visY1 = cropY + visHPct / 2;
  const xRange = visX1 - visX0;
  const yRange = visY1 - visY0;
  if (xRange <= 0 || yRange <= 0) return false;

  for (const f of faces) {
    if (!f || typeof f.x !== 'number') continue;
    const fLeft   = (f.x / imgW) * 100;
    const fRight  = ((f.x + f.width) / imgW) * 100;
    const fTop    = (f.y / imgH) * 100;
    const fBottom = ((f.y + f.height) / imgH) * 100;
    const sLeftPos   = (fLeft   - visX0) / xRange;
    const sRightPos  = (fRight  - visX0) / xRange;
    const sTopPos    = (fTop    - visY0) / yRange;
    const sBottomPos = (fBottom - visY0) / yRange;
    if (sLeftPos   < inset || sRightPos  > 1 - inset) return false;
    if (sTopPos    < inset || sBottomPos > 1 - inset) return false;
  }
  return true;
}
